import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle,
  Zap,
  Award,
  Users,
  TrendingUp,
  Star,
  Droplets,
  Sun,
  Battery,
  Factory,
} from 'lucide-react';
import { products, services, projects, testimonials, stats } from '../data/mockData';

const Home = () => {
  const [animatedStats, setAnimatedStats] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setAnimatedStats(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-50 via-white to-green-50">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1639135723583-5c84eb8b6198?w=1920"
            alt="Solar Panels"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#004534]/90 to-[#0C6951]/80"></div>
        </div>

        {/* Solar Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-20">
          <div className="solar-grid-pattern-hero w-full h-full"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="animate-fadeInUp">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Power Your Future with
              <span className="block text-[#D3FF62] mt-2">Clean Solar Energy</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 mb-8 max-w-3xl mx-auto">
              13+ Years of Excellence | 5000+ Projects | Serving Karnataka & Goa
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link
                to="/contact"
                className="bg-[#D3FF62] hover:bg-[#c4f052] text-[#004534] px-8 py-4 rounded-full font-semibold text-lg flex items-center space-x-2 transition-all duration-300 hover:scale-105 shadow-2xl"
              >
                <span>Get Free Consultation</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/products"
                className="bg-white/10 backdrop-blur-sm border-2 border-white hover:bg-white/20 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
              >
                Explore Products
              </Link>
            </div>
          </div>

          {/* Floating Icons Animation */}
          <div className="absolute top-20 left-10 animate-float">
            <Sun className="w-16 h-16 text-yellow-300 opacity-70" />
          </div>
          <div className="absolute bottom-20 right-10 animate-float delay-1000">
            <Battery className="w-16 h-16 text-green-300 opacity-70" />
          </div>
        </div>

        <style jsx>{`
          .solar-grid-pattern-hero {
            background-image: 
              linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
              linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px);
            background-size: 100px 100px;
          }
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(50px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-20px);
            }
          }
          .animate-fadeInUp {
            animation: fadeInUp 1s ease-out;
          }
          .animate-float {
            animation: float 3s ease-in-out infinite;
          }
          .delay-1000 {
            animation-delay: 1s;
          }
        `}</style>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-20 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = {
                CheckCircle,
                Award,
                Users: Factory,
                ThumbsUp: TrendingUp,
                Leaf: Sun,
              }[stat.icon] || CheckCircle;

              return (
                <div
                  key={index}
                  className={`text-center transform transition-all duration-700 ${
                    animatedStats ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#1e3a8a] to-[#22c55e] rounded-full mb-4 shadow-lg">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-4xl md:text-5xl font-bold text-[#004534] mb-2">
                    {stat.number}
                  </h3>
                  <p className="text-gray-600 font-medium">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Why Choose Shri Durga Enterprises?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your trusted partner in solar energy solutions with proven expertise and commitment to excellence
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: '13+ Years Experience',
                description: 'Two decades of excellence in solar industry with deep expertise and market knowledge.',
              },
              {
                icon: Users,
                title: '5000+ Happy Clients',
                description: 'Served residential, commercial, industrial, and agricultural sectors across three states.',
              },
              {
                icon: CheckCircle,
                title: 'Quality Assurance',
                description: 'Premium products with comprehensive warranties and certified installations.',
              },
              {
                icon: Zap,
                title: 'Turnkey Solutions',
                description: 'Complete end-to-end service from design to installation and maintenance.',
              },
              {
                icon: TrendingUp,
                title: 'Maximum Savings',
                description: 'Optimize your energy costs with customized solutions for maximum ROI.',
              },
              {
                icon: Sun,
                title: 'Eco-Friendly',
                description: 'Contributing to a greener planet by reducing carbon emissions significantly.',
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                <div className="bg-gradient-to-br from-[#1e3a8a] to-[#22c55e] w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-md">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#004534] mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Our Solar Products
            </h2>
            <p className="text-xl text-gray-600">
              Premium quality solar solutions for every need
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {products.slice(0, 3).map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 group"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-[#22c55e] text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {product.category}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-[#004534] mb-3">{product.name}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{product.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {product.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 font-medium">
                    Capacity: {product.capacity}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/products"
              className="inline-flex items-center space-x-2 bg-[#22c55e] hover:bg-[#16a34a] text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
            >
              <span>View All Products</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600">
              Comprehensive solar solutions for every sector
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {services.slice(0, 3).map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-[#004534] mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/services"
              className="inline-flex items-center space-x-2 bg-[#22c55e] hover:bg-[#16a34a] text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
            >
              <span>View All Services</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Real experiences from our satisfied customers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                <div className="flex items-center mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover mr-4 border-4 border-white shadow-md"
                  />
                  <div>
                    <h4 className="font-bold text-[#004534] text-lg">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                    <p className="text-xs text-gray-500">{testimonial.location}</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed italic">"{testimonial.text}"</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/testimonials"
              className="inline-flex items-center space-x-2 bg-[#22c55e] hover:bg-[#16a34a] text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
            >
              <span>Read More Reviews</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#004534] to-[#0C6951] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="solar-grid-pattern-cta w-full h-full"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Go Solar?
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Join 5000+ satisfied customers. Get a free consultation and customized solar solution for your needs.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-2 bg-[#D3FF62] hover:bg-[#c4f052] text-[#004534] px-10 py-5 rounded-full font-bold text-xl transition-all duration-300 hover:scale-105 shadow-2xl"
          >
            <span>Get Free Consultation</span>
            <ArrowRight className="w-6 h-6" />
          </Link>
        </div>
        <style jsx>{`
          .solar-grid-pattern-cta {
            background-image: 
              linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px),
              linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px);
            background-size: 80px 80px;
          }
        `}</style>
      </section>
    </div>
  );
};

export default Home;
